import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Button } from '@mui/material';
import useTheme from '../hooks/useTheme';
import ReportModal from './ReportModal';
import { fetchWithCache, fetchAllPages, fetchMultipleEndpoints, performanceMonitor, apiCache } from '../utils/apiCache';

export default function Dashboard() {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total_signups: 0,
    signups_last_7_days: 0,
    signups_last_30_days: 0,
    avg_completed_discovery_duration: 0,
    completion_rate: 0,
    email_drop_off_rate: 0,
    email_delivery_rate: 0,
    total_completed: 0,
    total_with_email: 0,
    total_dropped_off: 0
  });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🚀 Starting parallel Dashboard fetch...');
        
        // Fetch analytics (single object) and paginated reports in parallel for maximum performance
        const [analyticsData, reportsData] = await Promise.all([
          performanceMonitor.measureApiCall(
            'Dashboard/analytics',
            fetchWithCache('https://api.uxhq.com/analytics')
          ),
          performanceMonitor.measureApiCall(
            'Dashboard/reports_with_users-paginated',
            fetchAllPages('https://api.uxhq.com/reports_with_users')
          )
        ]);

        console.log('Analytics data received:', analyticsData);
        console.log('Duration value:', analyticsData?.avg_completed_discovery_duration);
        console.log(`📊 Dashboard: Loaded ${reportsData.length} total reports`);
        
        // Ensure analytics data is an object, not an array
        const statsData = Array.isArray(analyticsData) ? (analyticsData[0] || {}) : (analyticsData || {});
        setStats(statsData);

        // Get the last 10 reports sorted by creation date
        const recentReports = Array.isArray(reportsData) 
          ? reportsData
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 10)
          : [];
        setReports(recentReports);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00:00';
    
    // Ensure seconds is a number
    const totalSeconds = Math.round(parseFloat(seconds));
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleViewReport = useCallback((report) => {
    const date = new Date(report.createdAt);
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = date.toTimeString().split(' ')[0]; // HH:MM:SS
    
    setSelectedReport({
      userId: report.user_id,
      reportDate: dateStr,
      reportTime: timeStr
    });
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedReport(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Clear cache and fetch fresh data in parallel
      console.log('🔄 Refreshing Dashboard with fresh data...');
      apiCache.clearAll(); // Clear all cache for fresh fetch
      
      const [analyticsData, reportsData] = await Promise.all([
        performanceMonitor.measureApiCall(
          'Dashboard/analytics-refresh',
          fetchWithCache('https://api.uxhq.com/analytics')
        ),
        performanceMonitor.measureApiCall(
          'Dashboard/reports_with_users-refresh-paginated',
          fetchAllPages('https://api.uxhq.com/reports_with_users')
        )
      ]);

      console.log(`📊 Dashboard Refresh: Loaded ${reportsData.length} total reports`);
      
      // Ensure analytics data is an object, not an array
      const statsData = Array.isArray(analyticsData) ? (analyticsData[0] || {}) : (analyticsData || {});
      setStats(statsData);
      
      const recentReports = Array.isArray(reportsData) 
        ? reportsData
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 10)
        : [];
      setReports(recentReports);
    } catch (err) {
      console.error('Error refreshing dashboard data:', err);
      setError('Failed to refresh dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const kpiCards = [
    { label: 'TOTAL EMAIL SIGNUPS', value: stats.total_signups || 0 },
    { label: 'SIGNUPS (LAST 7 DAYS)', value: stats.signups_last_7_days || 0 },
    { label: 'SIGNUPS (LAST 30 DAYS)', value: stats.signups_last_30_days || 0 },
    { label: 'AV. COMPLETED DISCOVERY DURATION', value: stats.avg_completed_discovery_duration ? formatDuration(stats.avg_completed_discovery_duration) : '0:00:00' },
    { label: 'COMPLETION RATE', value: `${stats.completion_rate || 0}%` },
    { label: 'EMAIL DROP OFF RATE', value: `${stats.email_drop_off_rate || 0}%` },
    { label: 'EMAIL DELIVERY RATE', value: `${stats.email_delivery_rate || 0}%` },
    { label: 'TOTAL COMPLETED', value: stats.total_completed || 0 }
  ];

  if (loading) {
    return (
      <Box sx={theme.styles.loadingContainer}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: theme.textSecondaryColor }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={theme.styles.pageContainer}>
        <Card sx={{ ...theme.styles.card, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleRefresh} sx={theme.styles.button}>
            Try Again
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={theme.styles.pageContainer}>
      {/* KPI Cards - First Row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {kpiCards.slice(0, 4).map((kpi, index) => (
          <Card key={index} sx={{ 
            flex: 1, 
            minWidth: 250, 
            p: 3, 
            ...theme.styles.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Typography variant="h3" fontWeight={700} sx={{ color: theme.textColor, mb: 1, fontSize: { xs: 24, sm: 28, md: 32 } }}>
              {kpi.value}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontWeight: 500, fontSize: 14 }}>
              {kpi.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* KPI Cards - Second Row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        {kpiCards.slice(4, 8).map((kpi, index) => (
          <Card key={index + 4} sx={{ 
            flex: 1, 
            minWidth: 250, 
            p: 3, 
            ...theme.styles.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Typography variant="h3" fontWeight={700} sx={{ color: theme.textColor, mb: 1, fontSize: { xs: 24, sm: 28, md: 32 } }}>
              {kpi.value}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontWeight: 500, fontSize: 14 }}>
              {kpi.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Last 10 Reports Table */}
      <Card sx={theme.styles.card}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.borderColor}` }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: theme.textColor }}>
            LAST 10 REPORTS
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ ...theme.styles.card, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={theme.styles.tableHeader}>
                <TableCell sx={theme.styles.tableHeaderCell}>EMAIL</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>NAME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>DATE</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>TIME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>ROLE</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>REPORT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length > 0 ? reports.map((report, index) => (
                <TableRow key={report.report_id} hover>
                  <TableCell sx={theme.styles.tableCell}>{report.user_email || '-'}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.name && report.name.length > 20 ? `${report.name.substring(0, 20)}...` : report.name || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: '2-digit' 
                    }) : '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.createdAt ? new Date(report.createdAt).toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    }) : '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.role && report.role.length > 20 ? `${report.role.substring(0, 20)}...` : report.role || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      sx={theme.styles.link}
                      onClick={() => handleViewReport(report)}
                    >
                      View Report
                    </Typography>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: theme.textSecondaryColor, py: 4 }}>
                    No reports available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      
      {/* Report Modal */}
      <ReportModal
        open={modalOpen}
        onClose={handleCloseModal}
        userId={selectedReport?.userId}
        reportDate={selectedReport?.reportDate}
        reportTime={selectedReport?.reportTime}
      />
    </Box>
  );
}